import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "wouter";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Bell, BellOff, ExternalLink, Plus, Trash2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
import { taskLinkLabel, type TaskLink } from "@shared/taskLinks";
import { taskLinkHref } from "@shared/recordHref";
import type { TaskRow } from "./TaskList";
import { RecordLinkPicker } from "./RecordLinkPicker";

/**
 * Everything that is not capture.
 *
 * The quick-add bar deliberately carries only what you need in the two seconds
 * you are writing something down. Priority, notes, status and re-scheduling are
 * refinements, and they live here.
 */

/** A UTC instant as the local wall-clock string <input type=datetime-local> wants. */
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function TaskDetailSheet({ id, onClose }: { id: string | null; onClose: () => void }) {
  const qc = useQueryClient();

  // By id, not found in the viewer's own list: record pages open this panel
  // too, and a task on Barnes's page may be a teammate's.
  const { data: task = null, isLoading } = useQuery<TaskRow>({
    queryKey: ["/api/tasks", id],
    queryFn: async () => (await apiRequest("GET", `/api/tasks/${id}`)).json(),
    enabled: Boolean(id),
  });

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [when, setWhen] = useState("");
  const [remind, setRemind] = useState(false);

  // Reload the draft whenever a different task opens, so one task's edits
  // cannot be saved onto another.
  useEffect(() => {
    setTitle(task?.title ?? "");
    setDescription(task?.description ?? "");
    setWhen(toLocalInput(task?.dueDate));
    setRemind(Boolean(task?.remindAt));
  }, [task?.id, task?.title, task?.description, task?.dueDate, task?.remindAt]);

  // The panel opens from record pages as well as the Tasks page, so an edit
  // here refreshes those too: open items, the bell, the Prospects next step.
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["/api/tasks"] });
    qc.invalidateQueries({ queryKey: ["/api/reminders"] });
    qc.invalidateQueries({ queryKey: ["record-summary"] });
    qc.invalidateQueries({ queryKey: ["entityReminders"] });
    qc.invalidateQueries({ queryKey: ["/api/prospects"] });
  };

  const patch = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await apiRequest("PATCH", `/api/tasks/${id}`, body)).json(),
    onSuccess: invalidate,
    onError: (e: any) =>
      toast.error(String(e.message ?? "").replace(/^\d+:\s*/, "") || "Could not save."),
  });

  // Always the full list: the server replaces the set, so nothing half-applies.
  // The saved list goes straight into the cache, so the next pick or removal is
  // built on it rather than on the list from before this save; the picker and
  // the × buttons stay disabled until it lands.
  const saveLinks = useMutation({
    mutationFn: async (next: TaskLink[]): Promise<{ links: TaskLink[] }> =>
      (await apiRequest("PUT", `/api/tasks/${id}/links`, {
        links: next.map(({ kind, id: linkId }) => ({ kind, id: linkId })),
      })).json(),
    onSuccess: (saved) => {
      qc.setQueryData(["/api/tasks", id], (old: TaskRow | undefined) => (old ? { ...old, links: saved.links } : old));
      invalidate();
    },
    onError: (e: any) =>
      toast.error(String(e.message ?? "").replace(/^\d+:\s*/, "") || "Could not save the links."),
  });

  const remove = useMutation({
    mutationFn: async () => (await apiRequest("DELETE", `/api/tasks/${id}`)).json(),
    onSuccess: () => {
      invalidate();
      onClose();
      toast.success("Deleted.");
    },
  });

  /** Send both columns together — one "when" plus a bell is the whole model. */
  const saveWhen = (nextWhen: string, nextRemind: boolean) => {
    const iso = nextWhen ? new Date(nextWhen).toISOString() : null;
    patch.mutate({ dueDate: iso, remindAt: iso && nextRemind ? iso : null });
  };

  return (
    <Sheet open={Boolean(id)} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
        {isLoading || !task ? (
          <div className="space-y-3 pt-6">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
          <>
            <SheetHeader className="mb-4 border-b border-border pb-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
                {task.remindAt ? "Reminder" : "Task"}
              </div>
              <SheetTitle className="sr-only">{task.title}</SheetTitle>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => title.trim() && title !== task.title && patch.mutate({ title: title.trim() })}
                className="h-auto border-0 px-0 text-lg font-semibold shadow-none focus-visible:ring-0"
              />
            </SheetHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1.5">
                  <span className="text-xs text-muted-foreground">Status</span>
                  <Select
                    value={task.status}
                    onValueChange={(v) => patch.mutate({ status: v })}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">To do</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                      <SelectItem value="completed">Done</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </label>

                <label className="space-y-1.5">
                  <span className="text-xs text-muted-foreground">Priority</span>
                  <Select
                    value={task.priority}
                    onValueChange={(v) => patch.mutate({ priority: v })}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
              </div>

              <div className="space-y-1.5">
                <span className="text-xs text-muted-foreground">When</span>
                <div className="flex items-center gap-2">
                  <Input
                    type="datetime-local"
                    value={when}
                    onChange={(e) => {
                      setWhen(e.target.value);
                      saveWhen(e.target.value, remind);
                    }}
                    className="h-8 text-sm"
                  />
                  <button
                    type="button"
                    disabled={!when}
                    onClick={() => {
                      const next = !remind;
                      setRemind(next);
                      saveWhen(when, next);
                    }}
                    title={when ? (remind ? "Will remind you" : "No alarm") : "Pick a date first"}
                    className={cn(
                      "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-xs transition-colors",
                      !when && "cursor-not-allowed opacity-40",
                      remind
                        ? "border-primary/40 bg-primary/12 text-primary-text"
                        : "border-border text-muted-foreground hover:bg-muted",
                    )}
                  >
                    {remind ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
                    {remind ? "Reminds" : "Silent"}
                  </button>
                </div>
                {when && !remind && (
                  <p className="text-xs text-muted-foreground">
                    Due at this time, but it will not email or notify you.
                  </p>
                )}
              </div>

              <label className="block space-y-1.5">
                <span className="text-xs text-muted-foreground">Notes</span>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onBlur={() =>
                    description !== (task.description ?? "") &&
                    patch.mutate({ description: description || null })
                  }
                  placeholder="Anything worth remembering…"
                  className="min-h-[90px] text-sm"
                />
              </label>

              {/* Always shown, so a link can be added after the fact. */}
              <div className="space-y-1.5">
                <span className="text-xs text-muted-foreground">Linked to</span>
                {task.links.length > 0 && (
                  <ul className="divide-y divide-border/60 rounded-md border border-border">
                    {task.links.map((l) => (
                      <li key={`${l.kind}:${l.id}`} className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm">
                        <span className="min-w-0 truncate">{taskLinkLabel(l)}</span>
                        <span className="flex shrink-0 items-center gap-2">
                          <Link href={taskLinkHref(l)} className="inline-flex items-center gap-1 text-xs text-primary-text hover:underline">
                            Open <ExternalLink className="h-3 w-3" />
                          </Link>
                          <button
                            type="button"
                            aria-label={`Unlink ${taskLinkLabel(l)}`}
                            disabled={saveLinks.isPending}
                            onClick={() => saveLinks.mutate(task.links.filter((x) => !(x.kind === l.kind && x.id === l.id)))}
                            className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <RecordLinkPicker value={task.links} onChange={(next) => saveLinks.mutate(next)} disabled={saveLinks.isPending}>
                  <button
                    type="button"
                    disabled={saveLinks.isPending}
                    className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border/70 px-2 text-xs text-muted-foreground hover:bg-muted"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Link
                  </button>
                </RecordLinkPicker>
              </div>

              {task.assigneeName && (
                <div className="text-xs text-muted-foreground">
                  Assigned to {task.assigneeName}
                </div>
              )}

              <div className="border-t border-border pt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => remove.mutate()}
                  disabled={remove.isPending}
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  Delete
                </Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
